/**
 * @fileoverview Number porting — sessionStorage helpers (`NpSessionKeys` on globalThis).
 *
 * Two keys per customer (suffix = Shopify customer id, e.g. `"123"`):
 *
 * **Account handoff — `np_account_handoff_{customerId}`**
 * - **Purpose:** Records that the user *explicitly* navigated here from account / eSIM UI with
 *   intent to pre-select that line (same payload shape as before: iccid, name, msisdn, …).
 * - **Written by:** Account snippets, eSIM details, and the number-porting modal opener — never by
 *   in-wizard card clicks (those use flow selection only).
 * - **Read by:** `numberPortingFlow` once on init: applies to Alpine state, copies into flow key
 *   for refresh safety, then removes handoff so it does not behave like a sticky wizard selection.
 *
 * **Flow selection — `np_flow_selection_{customerId}`**
 * - **Purpose:** Working eSIM choice for the *current* port-in or port-out session (schedule step,
 *   PAC generation, refresh on `#schedule` / select views).
 * - **Written by:** Wizard `selectEsim`, grid continue handlers, and (after handoff consume) the
 *   same payload mirrored from account handoff.
 * - **Cleared when:** Leaving transfer mode, closing the modal, or starting a fresh bring intro
 *   without account handoff — see `sections/number-porting-intro.liquid`.
 *
 * @see sections/number-porting-intro.liquid — Alpine `numberPortingFlow`
 */
(function (g) {
  'use strict';

  var HANDOFF_PREFIX = 'np_account_handoff_';
  var FLOW_PREFIX = 'np_flow_selection_';

  /**
   * SessionStorage key for the one-shot account → number-porting handoff payload.
   *
   * @param {string|number} customerId - Shopify customer id (stringified in the key).
   * @returns {string} Full key, e.g. `np_account_handoff_123`.
   */
  function handoffKey(customerId) {
    return HANDOFF_PREFIX + String(customerId);
  }

  /**
   * SessionStorage key for the in-wizard eSIM selection (port-in / port-out working state).
   *
   * @param {string|number} customerId - Shopify customer id (stringified in the key).
   * @returns {string} Full key, e.g. `np_flow_selection_123`.
   */
  function flowKey(customerId) {
    return FLOW_PREFIX + String(customerId);
  }

  /**
   * Parses JSON from sessionStorage; returns null on missing, empty, or invalid JSON.
   *
   * @param {string} key - Full storage key.
   * @returns {object|null} Parsed object, or null.
   */
  function readJson(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (raw == null || raw === '') return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * Stringifies and stores an object under the given key.
   *
   * @param {string} key - Full storage key.
   * @param {object} obj - Value to persist (JSON-serializable).
   * @returns {boolean} True if `setItem` succeeded.
   */
  function setJson(key, obj) {
    try {
      sessionStorage.setItem(key, JSON.stringify(obj));
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Removes a key; ignores storage errors (private, shared by clear helpers).
   *
   * @param {string} key - Full storage key.
   */
  function removeKey(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {}
  }

  /**
   * Saves the wizard’s current eSIM choice for schedule / PAC / mid-flow refresh.
   *
   * @param {string|number} customerId - Shopify customer id.
   * @param {object} obj - eSIM summary (e.g. iccid, name, msisdn, installed).
   * @returns {boolean} Whether storage succeeded.
   */
  function setFlowSelection(customerId, obj) {
    return setJson(flowKey(customerId), obj);
  }

  /**
   * Reads the in-wizard eSIM selection, if any.
   *
   * @param {string|number} customerId - Shopify customer id.
   * @returns {object|null} Parsed payload or null.
   */
  function getFlowSelection(customerId) {
    return readJson(flowKey(customerId));
  }

  /**
   * Clears flow selection (e.g. when leaving transfer-out or resetting the modal).
   *
   * @param {string|number} customerId - Shopify customer id.
   */
  function clearFlowSelection(customerId) {
    removeKey(flowKey(customerId));
  }

  /**
   * Reads account handoff without consuming it (caller may clear after apply).
   *
   * @param {string|number} customerId - Shopify customer id.
   * @returns {object|null} Parsed handoff payload or null.
   */
  function getAccountHandoff(customerId) {
    return readJson(handoffKey(customerId));
  }

  /**
   * Writes account handoff (typically right before navigating to `/pages/number-porting` or opening the modal).
   *
   * @param {string|number} customerId - Shopify customer id.
   * @param {object} obj - eSIM payload for pre-selection.
   * @returns {boolean} Whether storage succeeded.
   */
  function setAccountHandoff(customerId, obj) {
    return setJson(handoffKey(customerId), obj);
  }

  /**
   * Removes account handoff after apply or on modal close.
   *
   * @param {string|number} customerId - Shopify customer id.
   */
  function clearAccountHandoff(customerId) {
    removeKey(handoffKey(customerId));
  }

  g.NpSessionKeys = {
    handoffKey: handoffKey,
    flowKey: flowKey,
    readJson: readJson,
    setJson: setJson,
    remove: removeKey,
    setFlowSelection: setFlowSelection,
    getFlowSelection: getFlowSelection,
    clearFlowSelection: clearFlowSelection,
    getAccountHandoff: getAccountHandoff,
    setAccountHandoff: setAccountHandoff,
    clearAccountHandoff: clearAccountHandoff,
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
